import { useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react';
import type { Participant, RoomStatus, SlashCommandInfo, UserProfile } from '@shared/protocol';
import type { Upload } from '../hooks/useUploads';
import { fileRef, referencedPaths } from '../lib/files';
import { SendIcon, StopIcon } from './icons';

export interface ComposerHandle {
  /**
   * Inserts a reference to `path` at the caret as its own word and focuses
   * the box. A file the message already references is not added twice.
   */
  reference: (path: string) => void;
}

interface Props {
  status: RoomStatus;
  queueDepth: number;
  connected: boolean;
  error?: string;
  me?: UserProfile;
  participants: Participant[];
  onSend: (text: string) => void;
  /** Offered while the message is a lone "/word". */
  commands: SlashCommandInfo[];
  onInterrupt: () => void;
  onTyping: (active: boolean) => void;
  onDraft: (text: string) => void;
  onDismissError: () => void;
  /** Uploads in flight, shown above the box until their reference lands. */
  uploads: Upload[];
  onDismissUpload: (id: string) => void;
  /** The files the message references, reported whenever that set changes. */
  onReferencesChange: (paths: Set<string>) => void;
  ref?: Ref<ComposerHandle>;
}

const BUSY: RoomStatus[] = ['thinking', 'compacting', 'awaiting', 'queued'];

const MAX_SUGGESTIONS = 8;

/** Commands matching a "/query" being typed: prefix matches first, then the rest. */
function matchCommands(commands: SlashCommandInfo[], text: string): SlashCommandInfo[] {
  const m = /^\/(\S*)$/.exec(text);
  if (!m) return [];
  const q = m[1]!.toLowerCase();
  const starts = commands.filter((c) => c.name.toLowerCase().startsWith(q));
  const contains = q ? commands.filter((c) => !c.name.toLowerCase().startsWith(q) && c.name.toLowerCase().includes(q)) : [];
  return [...starts, ...contains].slice(0, MAX_SUGGESTIONS);
}

function typingLabel(participants: Participant[], meId?: string): string | null {
  const names = participants
    .filter((p) => p.typingSince && p.id !== meId)
    .map((p) => p.name.split(' ')[0]!);

  if (names.length === 0) return null;
  if (names.length === 1) return `${names[0]} is typing…`;
  if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`;
  return `${names.length} people are typing…`;
}

export function Composer({
  status,
  queueDepth,
  connected,
  error,
  me,
  participants,
  onSend,
  commands,
  onInterrupt,
  onTyping,
  onDraft,
  onDismissError,
  uploads,
  onDismissUpload,
  onReferencesChange,
  ref,
}: Props) {
  const [text, setText] = useState('');
  // The state value lags a render behind; several references dropped at once
  // must each see the text the previous one produced.
  const textRef = useRef('');
  const pendingCaret = useRef<number | null>(null);
  // Reported only on change, so the parent does not re-render per keystroke.
  const refsKey = useRef('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const typingRef = useRef(false);
  const typingTimer = useRef<number | undefined>(undefined);

  const busy = BUSY.includes(status);

  const [pick, setPick] = useState(0);
  /** The text the menu was dismissed at; it reopens once the text changes. */
  const [dismissedAt, setDismissedAt] = useState<string | null>(null);
  const suggestions = dismissedAt === text ? [] : matchCommands(commands, text);
  const picked = suggestions[Math.min(pick, suggestions.length - 1)];

  const complete = (cmd: SlashCommandInfo) => {
    onChange(`/${cmd.name} `);
    inputRef.current?.focus();
  };

  // Grow with the content, up to the CSS max-height. The scrollbar only
  // appears once it is capped -- otherwise sub-pixel rounding shows an empty
  // one on a single line.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
    el.style.overflowY = el.scrollHeight > el.clientHeight + 1 ? 'auto' : 'hidden';
  }, [text]);

  const signalTyping = (active: boolean) => {
    if (typingRef.current === active) return;
    typingRef.current = active;
    onTyping(active);
  };

  const reportReferences = (value: string) => {
    const paths = referencedPaths(value);
    const key = [...paths].sort().join('\n');
    if (key === refsKey.current) return;
    refsKey.current = key;
    onReferencesChange(paths);
  };

  const onChange = (value: string) => {
    textRef.current = value;
    setText(value);
    setPick(0);
    onDraft(value);
    reportReferences(value);
    if (typingTimer.current) clearTimeout(typingTimer.current);

    if (value.trim()) {
      signalTyping(true);
      typingTimer.current = window.setTimeout(() => signalTyping(false), 3000);
    } else {
      signalTyping(false);
    }
  };

  const submit = () => {
    const value = text.trim();
    if (!value || !connected) return;
    onSend(value);
    textRef.current = '';
    setText('');
    reportReferences('');
    signalTyping(false);
    if (typingTimer.current) clearTimeout(typingTimer.current);
  };

  useEffect(() => () => { if (typingTimer.current) clearTimeout(typingTimer.current); }, []);

  useImperativeHandle(ref, () => ({
    reference(path: string) {
      const el = inputRef.current;
      const value = textRef.current;
      if (referencedPaths(value).has(path)) {
        el?.focus();
        return;
      }
      const token = fileRef(path);
      const start = pendingCaret.current ?? el?.selectionStart ?? value.length;
      const end = pendingCaret.current ?? el?.selectionEnd ?? value.length;
      const before = value.slice(0, start);
      const after = value.slice(end);
      const lead = before && !/\s$/.test(before) ? ' ' : '';
      const trail = /^\s/.test(after) ? '' : ' ';
      const head = before + lead + token + trail;
      onChange(head + after);

      const caret = head.length;
      pendingCaret.current = caret;
      requestAnimationFrame(() => {
        pendingCaret.current = null;
        el?.focus();
        el?.setSelectionRange(caret, caret);
      });
    },
  }));

  const others = typingLabel(participants, me?.id);

  return (
    <div className="composer">
      <div className="composer__inner">
        {error && (
          <div className="composer__banner composer__banner--error" role="alert">
            <span style={{ flex: 1 }}>{error}</span>
            <button className="rail__signout" onClick={onDismissError}>
              dismiss
            </button>
          </div>
        )}

        {!connected && (
          <div className="composer__banner">
            Reconnecting… your message will send once the link is back.
          </div>
        )}

        {/* The whole box is the input's hit area: a click on its padding
            focuses the textarea instead of doing nothing. */}
        {uploads.length > 0 && (
          <div className="composer__uploads">
            {uploads.map((u) => (
              <span className={`upload-chip ${u.error ? 'upload-chip--failed' : ''}`} key={u.id}>
                <span className="upload-chip__name">{u.name}</span>
                <span className="upload-chip__state">
                  {u.error ?? `${u.total ? Math.round((u.loaded / u.total) * 100) : 0}%`}
                </span>
                {u.error && (
                  <button
                    className="upload-chip__dismiss"
                    onClick={() => onDismissUpload(u.id)}
                    aria-label={`Dismiss ${u.name}`}
                  >
                    ×
                  </button>
                )}
                {!u.error && (
                  <span
                    className="upload-chip__bar"
                    style={{ transform: `scaleX(${u.total ? u.loaded / u.total : 0})` }}
                  />
                )}
              </span>
            ))}
          </div>
        )}

        {picked && (
          <ul className="slash" role="listbox" aria-label="Commands">
            {suggestions.map((c) => (
              <li
                key={c.name}
                role="option"
                aria-selected={c === picked}
                className={`slash__item ${c === picked ? 'slash__item--active' : ''}`}
                // mousedown, not click: a click would blur the textarea first.
                onMouseDown={(e) => {
                  e.preventDefault();
                  complete(c);
                }}
                onMouseEnter={() => setPick(suggestions.indexOf(c))}
              >
                <span className="slash__name">
                  /{c.name}
                  {c.argumentHint && <span className="slash__hint"> {c.argumentHint}</span>}
                </span>
                <span className="slash__desc">{c.description}</span>
              </li>
            ))}
          </ul>
        )}

        <div
          className="composer__box"
          onMouseDown={(e) => {
            if (e.target !== e.currentTarget) return; // textarea, buttons
            e.preventDefault(); // keep focus from flickering to the div
            inputRef.current?.focus();
          }}
        >
          <textarea
            ref={inputRef}
            className="composer__input"
            rows={1}
            value={text}
            placeholder={
              busy ? 'Queue a follow-up while Claude works…' : 'Ask Claude anything…'
            }
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (picked && !e.nativeEvent.isComposing) {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  const step = e.key === 'ArrowDown' ? 1 : -1;
                  setPick((p) => (p + step + suggestions.length) % suggestions.length);
                  return;
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setDismissedAt(text);
                  return;
                }
                // Enter on a command typed out in full sends it; otherwise
                // both keys fill in the highlighted one.
                const exact = text.slice(1).toLowerCase() === picked.name.toLowerCase();
                if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey && !exact)) {
                  e.preventDefault();
                  complete(picked);
                  return;
                }
              }
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                submit();
              }
            }}
            onBlur={() => signalTyping(false)}
            aria-label="Message Claude"
          />

          {busy ? (
            <button
              className="icon-button icon-button--stop"
              onClick={onInterrupt}
              title="Stop the current turn"
              aria-label="Stop"
            >
              <StopIcon />
            </button>
          ) : (
            <button
              className="icon-button"
              onClick={submit}
              disabled={!text.trim() || !connected}
              title="Send"
              aria-label="Send"
            >
              <SendIcon />
            </button>
          )}
        </div>

        <div className="composer__foot">
          <span>
            <span className="composer__kbd">Enter</span> to send ·{' '}
            <span className="composer__kbd">Shift</span>+
            <span className="composer__kbd">Enter</span> for a new line ·{' '}
            <span className="composer__kbd">/</span> for commands
          </span>
          <span style={{ flex: 1 }} />
          {queueDepth > 0 && <span>{queueDepth} queued</span>}
          {others && <span style={{ color: 'var(--accent)' }}>{others}</span>}
        </div>
      </div>
    </div>
  );
}
